'use client';

import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import type { MemberLoginResponse } from '../../apis/client-side/member/type';
import { memberQueryKeys } from '../../queries/member/memberQueryKeys';
import { updateDataLayer } from '../../utils/gtm/datalayer-utils';
import useBroadcastChannel from '../../utils/useBroadcastChannel';
import type { AuthBroadcastChannelMessageType } from './useReceiveAuthBC';
export const browserUniqueId = Math.random().toString(36).substr(2, 9);
function useSendAuthBC() {
  const queryClient = useQueryClient();
  const {
    sendMessage
  } = useBroadcastChannel<AuthBroadcastChannelMessageType>('auth');
  const sendLoginMessage = useCallback(() => {
    sendMessage({
      type: 'login',
      payload: browserUniqueId
    });
    const timeoutid = setTimeout(() => {
      const unknownInfo = queryClient.getQueryData(memberQueryKeys.fetchMemberLoginInfo().queryKey);
      if (!unknownInfo) return;
      const memberLoginInfo = unknownInfo as MemberLoginResponse;
      if ('loginInfo' in memberLoginInfo) {
        let socialInfo = 'normal';
        if (memberLoginInfo.loginInfo?.userInfo?.socialYN === 'Y') {
          socialInfo = memberLoginInfo.loginInfo?.userInfo?.social;
        }
        updateDataLayer('login_success', {
          login_social_method: socialInfo.toLowerCase()
        });
      }
    }, 4000);
    return () => {
      clearTimeout(timeoutid);
    };
  }, [sendMessage]);
  const sendLogoutMessage = useCallback(() => {
    sendMessage({
      type: 'logout',
      payload: browserUniqueId
    });
  }, [sendMessage]);
  const sendJoinMessage = useCallback(() => {
    sendMessage({
      type: 'join',
      payload: browserUniqueId
    });
    const timeoutid_0 = setTimeout(() => {
      const unknownInfo_0 = queryClient.getQueryData(memberQueryKeys.fetchMemberLoginInfo().queryKey);
      if (!unknownInfo_0) return;
      const memberLoginInfo_0 = unknownInfo_0 as MemberLoginResponse;
      if ('loginInfo' in memberLoginInfo_0) {
        let socialInfo_0 = 'normal';
        if (memberLoginInfo_0.loginInfo?.userInfo?.socialYN === 'Y') {
          socialInfo_0 = memberLoginInfo_0.loginInfo?.userInfo?.social;
        }
        updateDataLayer('signup_success', {
          signup_social_method: socialInfo_0.toLowerCase()
        });
      }
    }, 4000);
    return () => {
      clearTimeout(timeoutid_0);
    };
  }, [sendMessage]);
  return {
    sendLoginMessage,
    sendLogoutMessage,
    sendJoinMessage
  };
}
export default useSendAuthBC;